import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { isEnabled, FEATURE_CHANGE_BUDGET_PHASE } from 'helpers/features';
import { styled } from '@mui/material/styles';
import { useHasAccessBudgetsGestion } from 'hooks/user';
import BudgetPhase from './BudgetPhase';
import BudgetPhaseDialog from './BudgetPhaseDialog';
import { Box } from '@mui/system';

const StyBudgetItem = styled('div', {
    name: 'StyBudgetItem',
})({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
    justifyContent: 'space-between',
    backgroundColor: '#EFEFEF',
    boxShadow: '0px 2px 2px #eee',
    borderRadius: 4,
    width: 380,
    transition: 'background-color 150ms ease',
    '&:hover': {
        backgroundColor: '#e1e1e1',
    },
});

const StyBudgetContent = styled('div', {
    name: 'StyBudgetContent',
})({
    padding: '1rem',
    maxWidth: '100%',
});

const StyBudgetName = styled('div', {
    name: 'StyBudgetName',
})({
    flexGrow: 1,
    paddingBottom: '1rem',
    maxHeight: 'inherit',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})(({ theme }) => ({
    color: theme.palette.primary.main,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
}));

const StyLink = styled(Link, {
    name: 'StyLink',
})({
    color: 'inherit',
    textDecoration: 'none',
});

type BudgetLibraryItemProps = {
    budgetProposal: BudgetProposalT;
    isDragging: boolean;
};

function BudgetLibraryItem({
    budgetProposal,
    isDragging,
}: BudgetLibraryItemProps): React.ReactElement {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [showPhaseDialog, setShowPhaseDialog] = useState(false);
    const { t } = useTranslation();
    const { id, phase, budgetName, exercice } = budgetProposal;
    const hasAccessBudgetsGestion = useHasAccessBudgetsGestion();

    const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget);
    };
    const closeMenu = () => {
        setAnchorEl(null);
    };

    const showSettings = isEnabled(FEATURE_CHANGE_BUDGET_PHASE);
    return (
        <>
            <StyLink
                to={`/budget_proposal/${id}?viewBy=resource`}
                as={isDragging ? Box : Link}
            >
                <StyBudgetItem>
                    <StyBudgetContent>
                        <StyBudgetName role="button" tabIndex={0}>
                            <Tooltip
                                title={`${budgetName} ${exercice}`}
                                placement="bottom"
                            >
                                <StyTitle variant="body1Bold">
                                    {`${budgetName} ${exercice}`}
                                </StyTitle>
                            </Tooltip>
                        </StyBudgetName>
                        <BudgetPhase phase={phase} />
                    </StyBudgetContent>
                    {hasAccessBudgetsGestion && showSettings ? (
                        <>
                            <IconButton onClick={openMenu} size="large">
                                <MoreVertIcon />
                            </IconButton>
                        </>
                    ) : null}
                </StyBudgetItem>
            </StyLink>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
            >
                <MenuItem
                    onClick={() => {
                        setShowPhaseDialog(true);
                        closeMenu();
                    }}
                >
                    {t('budgetProposal.changePhase')}
                </MenuItem>
            </Menu>
            <BudgetPhaseDialog
                budgetProposal={budgetProposal}
                open={showPhaseDialog}
                setOpen={setShowPhaseDialog}
            />
        </>
    );
}

export default BudgetLibraryItem;
